import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, UserPlus } from 'lucide-react';
import { useAuth } from '@/contexts/auth-context';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

interface SignUpFormProps {
  onToggleMode: () => void;
}

export const SignUpForm: React.FC<SignUpFormProps> = ({ onToggleMode }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isInvitationSignup, setIsInvitationSignup] = useState(false);
  const [invitationId, setInvitationId] = useState<string | null>(null);

  const { signUp } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check if this is an invitation signup
  useEffect(() => {
    const invitationEmail = searchParams.get('email');
    const invitationIdParam = searchParams.get('invitationId');
    
    if (invitationEmail) {
      setEmail(invitationEmail);
      setIsInvitationSignup(true);
    }
    
    if (invitationIdParam) {
      setInvitationId(invitationIdParam);
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      if (isInvitationSignup) {
        // For invitation signups, use Supabase's built-in auth
        const returnUrl = searchParams.get('returnUrl');
        
        // Use Supabase's built-in signUp (creates user properly)
        const { data, error: signUpError } = await supabase.auth.signUp({
          email: email.toLowerCase(),
          password: password,
          options: {
            data: {
              display_name: displayName || email.split('@')[0]
            }
          }
        });

        if (signUpError) {
          throw new Error(signUpError.message || 'Failed to create user account');
        }

        if (!data.user) {
          throw new Error('Failed to create user account');
        }

        // Send our custom verification code
        const { data: verificationData, error: verificationError } = await supabase.functions.invoke('send-verification-code', {
          body: {
            email: email.toLowerCase(),
            type: 'invitation',
            invitationId: invitationId,
            password: password // Pass the password for auto-login
          }
        });

        if (verificationError) {
          throw new Error('Failed to send verification code');
        }

        if (!verificationData?.success) {
          throw new Error(verificationData?.error || 'Failed to send verification code');
        }

        // Redirect to verification page
        const verificationUrl = `/verify-code?email=${encodeURIComponent(email)}&type=invitation&invitationId=${invitationId}&returnUrl=${encodeURIComponent(returnUrl || '')}`;
        navigate(verificationUrl);
      } else {
        // For direct signups (non-invitation)
        const { data, error: signUpError } = await supabase.auth.signUp({
          email: email.toLowerCase(),
          password: password,
          options: {
            data: {
              display_name: displayName || email.split('@')[0]
            }
          }
        });

        if (signUpError) {
          throw new Error(signUpError.message || 'Failed to create account');
        }

        if (!data.user) {
          throw new Error('Failed to create account');
        }

        // Send verification code for direct signup
        const { data: verificationData, error: verificationError } = await supabase.functions.invoke('send-verification-code', {
          body: {
            email: email.toLowerCase(),
            type: 'signup',
            password: password // Pass the password for auto-login
          }
        });

        if (verificationError) {
          throw new Error('Failed to send verification code');
        }

        if (!verificationData?.success) {
          throw new Error(verificationData?.error || 'Failed to send verification code');
        }

        // Redirect to verification page for direct signup
        const verificationUrl = `/verify-code?email=${encodeURIComponent(email)}&type=signup`;
        navigate(verificationUrl);
      }
    } catch (error) {
      console.error('Signup error:', error);
      setError(error instanceof Error ? error.message : 'Sign up failed');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center text-green-600">
            Check Your Email
          </CardTitle>
          <CardDescription className="text-center">
            We've sent you a confirmation link to complete your registration.
            You can close this window and check your email for the confirmation link.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            className="w-full"
          >
            Back to Home
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl font-bold text-center">
          {isInvitationSignup ? 'Complete Your Invitation' : 'Create Account'}
        </CardTitle>
        <CardDescription className="text-center">
          {isInvitationSignup 
            ? 'Set up your account to accept the invitation'
            : 'Join WattOS to get started'
          }
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="displayName">How You would Like Your Name to Be Displayed</Label>
            <Input
              id="displayName"
              type="text"
              placeholder="Enter how you'd like your name displayed"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
              disabled={loading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading || isInvitationSignup}
            />
            <p className="text-xs text-muted-foreground">
              Your company will be automatically detected from your email domain
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              placeholder="Create a password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              minLength={6}
            />
            <p className="text-xs text-muted-foreground">
              Must be at least 6 characters long
            </p>
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={loading || !email || !password || !displayName}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {isInvitationSignup ? 'Setting up account...' : 'Creating account...'}
              </>
            ) : (
              <>
                <UserPlus className="w-4 h-4 mr-2" />
                {isInvitationSignup ? 'Complete Setup' : 'Create Account'}
              </>
            )}
          </Button>

          {!isInvitationSignup && (
            <div className="text-center">
              <Button
                type="button"
                variant="link"
                onClick={onToggleMode}
                className="text-sm"
              >
                Already have an account? Sign in
              </Button>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
}; 